import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMeasure } from 'react-use';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@che-ins-ui/button';
import { Input } from '@che-ins-ui/input';
import { MAX_EMAILS_AMOUNT, MIN_INSURER_AGE } from '@/libs/constants';
import { Hint } from '@/components/common/Hint';
import { InputSuggestions } from '@/components/common/InputSuggestions';
import { InputError } from '@/components/common/InputError';
import { InputDropdpownPostfix } from '@/components/common/InputSuggestions/InputDropdpownPostfix';
import { emailsSelector, insurerFormsSelector, isFormDirtySelector, passengersFormsSelector, } from '@/models/forms/selectors';
import { formsActions } from '@/models/forms';
import { InsurerField, NameField, } from '@/models/forms/interface';
import { setInsurerName } from '@/models/forms/setInsurerName';
import { setInsurerBirthday } from '@/models/forms/setInsurerBirthday';
import { formatNameOnBlur } from '@/models/forms/helpers';
import { useIsMobileInputSuggestions, useIsMobileViewInsuranceForm, } from '../hooks';
import { useInsuranceStyles } from '../styles';
import { inputLabels, inputNames, inputPlaceholders } from './consts';
import { InsurerEmails } from './InsurerEmails';
import { FormFieldPhoneInput } from './FormFieldPhoneInput';
import { useFormStyles, useInsurerStyles } from './styles';
const animationTransition = {
    transition: { layout: { duration: 0.15 } },
};
export const INSURER_CONTAINER_ID = 'insurer-container';
export const Insurer = memo(() => {
    const dispatch = useDispatch();
    const classes = useInsurerStyles();
    const formClasses = useFormStyles();
    const insuranceClasses = useInsuranceStyles();
    const insurerNameRef = useRef(null);
    const [ref, { height }] = useMeasure();
    const isMobileFormView = useIsMobileViewInsuranceForm();
    const isMobileSuggestionsView = useIsMobileInputSuggestions();
    const insurer = useSelector(insurerFormsSelector);
    const passengers = useSelector(passengersFormsSelector);
    const emails = useSelector(emailsSelector);
    const isFormDirty = useSelector(isFormDirtySelector);
    const { fieldsDirty, fieldsErrors } = insurer;
    const suggestions = useMemo(() => {
        return passengers
            .filter((passenger) => passenger.age >= 18)
            .filter((passenger) => {
            const hasName = passenger.firstName.trim() && passenger.lastName.trim();
            const hasBirthday = passenger.birthday.trim();
            return hasName && hasBirthday;
        })
            .map((passenger) => ({
            _uuid: passenger._uuid,
            firstName: passenger.firstName,
            lastName: passenger.lastName,
            birthday: passenger.birthday,
            emoji: '',
            firstNameRu: '',
            lastNameRu: '',
            secondNameRu: '',
            fromNotebook: false,
        }));
    }, [passengers]);
    const hasSuggestions = suggestions.length > 0;
    const [hoveredSuggestion, setHoveredSuggestion] = useState(null);
    const [areSuggestionsShown, setAreSuggestionsShown] = useState(false);
    const [previewSuccess, setPreviewSuccess] = useState(false);
    const getFormError = (field) => !hoveredSuggestion &&
        (isFormDirty || fieldsDirty[field]) &&
        fieldsErrors[field];
    const formErrors = {
        lastName: getFormError(InsurerField.lastName),
        firstName: getFormError(InsurerField.firstName),
        birthday: getFormError(InsurerField.birthday),
    };
    const handleSuggestionClick = (suggestion) => {
        setHoveredSuggestion(null);
        setAreSuggestionsShown(false);
        const nameFields = [
            InsurerField.lastName,
            InsurerField.firstName,
        ];
        nameFields.forEach((field) => {
            setFieldDirty(field);
            setFieldNameValue(field, suggestion[field]);
        });
        setBirthday(suggestion.birthday);
        setPreviewSuccess(true);
        setTimeout(() => {
            setPreviewSuccess(false);
        }, 200);
    };
    const handleSuggestionHover = (suggestion) => {
        if (isMobileSuggestionsView) {
            return;
        }
        setHoveredSuggestion(suggestion);
    };
    const handleSuggestionBlur = () => {
        setHoveredSuggestion(null);
    };
    useEffect(() => {
        handleSuggestionBlur();
    }, []);
    const getInputsAppearance = () => {
        if (hoveredSuggestion) {
            return 'preview';
        }
        if (previewSuccess) {
            return 'preview-success';
        }
    };
    const appearance = getInputsAppearance();
    const setBirthday = (value) => dispatch(setInsurerBirthday(value));
    const rootAnimation = useMemo(() => (height ? { height: height + 30 } : {}), [height]);
    const setFieldDirty = (field) => dispatch(formsActions.setInsurerFieldDirty(field));
    const setFieldNameValue = (field, value) => dispatch(setInsurerName({ field, value }));
    const getInputField = (field) => {
        const isBirthdayField = field === InsurerField.birthday;
        const withSuggestions = [NameField.lastName].includes(field);
        const onChange = (value) => isBirthdayField
            ? setBirthday(value)
            : setFieldNameValue(field, value);
        const className = classes.fieldWrapper;
        const getValue = () => {
            if (hoveredSuggestion) {
                return hoveredSuggestion[field];
            }
            return insurer[field];
        };
        const getIsDisabled = () => {
            if (hoveredSuggestion) {
                return !hoveredSuggestion[field];
            }
            return false;
        };
        const getName = () => {
            if (withSuggestions && hasSuggestions) {
                return;
            }
            return inputNames[field];
        };
        const value = getValue();
        const getPostfix = () => {
            if (withSuggestions && hasSuggestions)
                return (_jsx(InputDropdpownPostfix, { isShown: hasSuggestions, isActive: areSuggestionsShown, onClick: () => setAreSuggestionsShown((prev) => !prev) }));
            return null;
        };
        return (_jsxs("div", { className: className, ref: withSuggestions ? insurerNameRef : undefined, children: [_jsx(Input, { value: value, onChange: hoveredSuggestion ? () => { } : onChange, name: getName(), label: inputLabels[field], shouldFitContent: true, placeholder: inputPlaceholders[field], appearance: appearance, invalid: Boolean(formErrors[field]), disabled: getIsDisabled(), postfix: getPostfix(), onFocus: () => {
                        if (withSuggestions && !insurer[field]?.trim()) {
                            setAreSuggestionsShown(true);
                        }
                    }, onBlur: () => {
                        setFieldDirty(field);
                        if (!isBirthdayField) {
                            onChange(formatNameOnBlur(insurer[field]));
                        }
                    } }), _jsx(InputError, { error: formErrors[field] }), withSuggestions && (_jsx(InputSuggestions, { ref: insurerNameRef, suggestions: suggestions, onHover: handleSuggestionHover, onBlur: handleSuggestionBlur, onClick: handleSuggestionClick, areSuggestionsShown: areSuggestionsShown, hideSuggestions: () => setAreSuggestionsShown(false) }))] }));
    };
    return (_jsx(motion.div, { layout: true, initial: rootAnimation, animate: rootAnimation, className: insuranceClasses.root, "data-insurer": true, transition: { duration: 0.15 }, id: INSURER_CONTAINER_ID, children: _jsx("div", { ref: ref, children: _jsxs(AnimatePresence, { initial: false, mode: "popLayout", children: [_createElement(motion.div, { layout: true, ...animationTransition, className: insuranceClasses.header, key: "header" },
                        _jsx("h3", { className: insuranceClasses.title, children: "\u0421\u0442\u0440\u0430\u0445\u043E\u0432\u0430\u0442\u0435\u043B\u044C" }),
                        _jsxs(Hint, { id: "whoCanBay", label: "\u0422\u0440\u0435\u0431\u043E\u0432\u0430\u043D\u0438\u044F \u043A \u043F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044E", title: "\u0422\u0440\u0435\u0431\u043E\u0432\u0430\u043D\u0438\u044F \u043A \u043F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044E", actionType: "agree", children: ["\u041A\u0443\u043F\u0438\u0442\u044C \u043F\u043E\u043B\u0438\u0441 \u043C\u043E\u0436\u0435\u0442 \u043A\u0430\u0436\u0434\u044B\u0439, \u043A\u043E\u043C\u0443 \u0438\u0441\u043F\u043E\u043B\u043D\u0438\u043B\u043E\u0441\u044C", ' ', MIN_INSURER_AGE, " \u043B\u0435\u0442. \u041F\u0440\u0438\u043E\u0431\u0440\u0435\u0441\u0442\u0438 \u0441\u0442\u0440\u0430\u0445\u043E\u0432\u043A\u0443 \u043C\u043E\u0436\u043D\u043E \u0434\u043B\u044F \u0441\u0435\u0431\u044F \u0438\u043B\u0438 \u043B\u044E\u0431\u043E\u0433\u043E \u0434\u0440\u0443\u0433\u043E\u0433\u043E \u0447\u0435\u043B\u043E\u0432\u0435\u043A\u0430"] })), _jsxs(motion.div, { layout: "position", ...animationTransition, className: classes.insurer, children: [getInputField(InsurerField.lastName), getInputField(InsurerField.firstName), getInputField(InsurerField.birthday), _jsx(FormFieldPhoneInput, {}), _jsx(InsurerEmails, {})] }), emails.length < MAX_EMAILS_AMOUNT && (_createElement(motion.div, { layout: true, className: formClasses.addButtonWrapper, initial: { opacity: 0.8, scale: 0.4 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.3 }, ...animationTransition, key: "button" },
                        _jsx(Button, { onClick: () => dispatch(formsActions.addEmail()), baseAppearance: "toggle-item-forms-page", appearance: "secondary", shouldFitContent: isMobileFormView, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C e-mail" })))] }) }) }));
});
